import { cn } from '@lib/cn';

/**
 * A switch that reflects server state.
 *
 * **Controlled only.** It used to hold its own `useState` and flip on click,
 * which was right for a mockup and is actively wrong now: a flag toggle that
 * moves before the write is confirmed tells an operator they changed production
 * when they may not have. A version-guarded write can be rejected outright by a
 * colleague's concurrent change, so the switch must not move until the API says
 * it did.
 *
 * `busy` is therefore not decoration — it is the state between the click and the
 * answer, and it is the only honest thing to render there.
 */
export function Toggle({
    on,
    onChange,
    disabled = false,
    busy = false,
    showLabel = false,
    'aria-label': ariaLabel = 'Toggle',
}: {
    on: boolean;
    /** Omit for a read-only switch — it then renders as disabled. */
    onChange?: (next: boolean) => void;
    disabled?: boolean;
    /** A write is in flight. Blocks input and dims, without moving the switch. */
    busy?: boolean;
    showLabel?: boolean;
    'aria-label'?: string;
}) {
    const inert = disabled || busy || onChange === undefined;

    return (
        <span className="inline-flex items-center gap-2.5">
            <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={ariaLabel}
                aria-busy={busy}
                // `busy` must not set the `disabled` attribute: the operator's
                // focus is *on this button* when the write starts, and a
                // disabled element is thrown out of the tab order — a keyboard
                // or screen-reader user would lose their place on every single
                // toggle. `aria-disabled` + the click guard blocks input (Enter
                // and Space arrive as clicks) while keeping focus where it is.
                disabled={disabled || onChange === undefined}
                aria-disabled={inert}
                onClick={() => {
                    if (!inert) onChange?.(!on);
                }}
                className={cn(
                    'relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors',
                    on ? 'bg-brand' : 'bg-line-strong',
                    busy && 'animate-pulse',
                    inert ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}
            >
                {/*
                 * `bg-white`, not `bg-surface`: the knob sits *on* the track,
                 * not on the page, and in the dark theme `surface` is the
                 * near-black card colour — which would render the knob as a
                 * hole punched through a terracotta track rather than as the
                 * thing that slid across it.
                 */}
                <span
                    className={cn(
                        'absolute top-0.5 size-[18px] rounded-full bg-white shadow transition-all',
                        on ? 'left-[18px]' : 'left-0.5',
                    )}
                />
            </button>
            {showLabel && (
                <span
                    className={cn(
                        'w-6 text-[12.5px] font-semibold',
                        on ? 'text-accent-icon' : 'text-content-muted',
                    )}
                >
                    {on ? 'On' : 'Off'}
                </span>
            )}
        </span>
    );
}
